import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatCard from "@/components/StatCard";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, Eye, Search, Link2, Gauge, RefreshCw, Globe, AlertTriangle,
  FileText, Sparkles, CheckCircle2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

export default function SeoReports() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [metrics, setMetrics] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [status, setStatus] = useState(null);
  const [backlinks, setBacklinks] = useState(null);

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);

  const load = useCallback((cid) => {
    if (!cid) return;
    api.get(`/clients/${cid}/seo/metrics`).then((r) => setMetrics(r.data));
    api.get(`/clients/${cid}/seo/latest`).then((r) => setSnapshot(r.data));
    api.get(`/clients/${cid}/seo/status`).then((r) => setStatus(r.data)).catch(() => {});
    api.get(`/clients/${cid}/seo/backlinks`).then((r) => setBacklinks(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(clientId); }, [clientId, load]);

  const analyze = async () => {
    if (!clientId) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/seo/analyze`);
      setSnapshot(data);
      if (data.ok) toast.success(`Crawled ${data.page_count} pages from ${data.domain}`);
      else toast.error(data.error || "Could not reach the website");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setAnalyzing(false); }
  };

  const latest = metrics[metrics.length - 1] || {};
  const prev = metrics[metrics.length - 2] || {};
  const delta = (a, b) => (b ? `${a >= b ? "+" : ""}${(((a - b) / b) * 100).toFixed(0)}% vs last` : "");

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">SEO & Website Reports</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500 text-sm">Visibility trends, traffic analytics & live website audit</p>
            {status && (
              <span data-testid="seo-source-badge" className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${status.source === "google" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {status.source === "google" ? "● Live Google data" : "● Sample data"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-48" data-testid="seo-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button onClick={analyze} disabled={analyzing} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="run-analysis-btn">
            <RefreshCw size={15} className={`mr-1.5 ${analyzing ? "animate-spin" : ""}`} /> {analyzing ? "Crawling…" : "Run Live Analysis"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard testid="seo-traffic" label="Organic Traffic" value={(latest.organic_traffic || 0).toLocaleString()} icon={TrendingUp} accent="#0EA5E9" sub={delta(latest.organic_traffic, prev.organic_traffic)} />
        <StatCard testid="seo-visibility" label="Visibility" value={`${latest.visibility || 0}%`} icon={Eye} accent="#3B82F6" sub={delta(latest.visibility, prev.visibility)} />
        <StatCard testid="seo-keywords" label="Keywords Top 10" value={latest.keywords_top10 || 0} icon={Search} accent="#10B981" sub={delta(latest.keywords_top10, prev.keywords_top10)} />
        <StatCard testid="seo-backlinks" label="Backlinks" value={latest.backlinks || 0} icon={Link2} accent="#8B5CF6" sub={delta(latest.backlinks, prev.backlinks)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-slate-200">
          <p className="font-display font-semibold text-slate-900 mb-4">Organic Traffic Trend</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={metrics}>
              <defs>
                <linearGradient id="traf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--tenant-primary)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--tenant-primary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="organic_traffic" stroke="var(--tenant-primary)" strokeWidth={2.5} fill="url(#traf)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <p className="font-display font-semibold text-slate-900 mb-4">Visibility & Rankings</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="visibility" stroke="#0EA5E9" strokeWidth={2.5} dot={false} name="Visibility %" />
              <Line type="monotone" dataKey="keywords_top10" stroke="#10B981" strokeWidth={2.5} dot={false} name="Keywords Top10" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Live website audit */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Globe size={18} style={{ color: "var(--tenant-primary)" }} />
            <p className="font-display font-semibold text-slate-900">Live Website Audit</p>
          </div>
          {snapshot?.fetched_at && <span className="text-xs text-slate-400">Last crawled {fmtDate(snapshot.fetched_at)}</span>}
        </div>

        {!snapshot ? (
          <div className="text-center py-12">
            <Sparkles size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No audit yet. Click <span className="font-medium">Run Live Analysis</span> to crawl the website in real time.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { l: "SEO Score", v: snapshot.seo_score ?? "—", c: "#10B981", I: Gauge },
                { l: "Pages Found", v: snapshot.page_count ?? 0, c: "#0EA5E9", I: FileText },
                { l: "Word Count", v: (snapshot.word_count || 0).toLocaleString(), c: "#3B82F6", I: FileText },
                { l: "Images", v: snapshot.image_count ?? 0, c: "#8B5CF6", I: Eye },
                { l: "New Pages", v: (snapshot.new_pages || []).length, c: "#F59E0B", I: TrendingUp },
              ].map((s) => (
                <div key={s.l} className="rounded-md border border-slate-200 p-3.5" data-testid="audit-stat">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400"><s.I size={14} style={{ color: s.c }} /> {s.l}</div>
                  <p className="font-display text-2xl font-bold text-slate-900 mt-1">{s.v}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Homepage content */}
              <div className="lg:col-span-1 space-y-3">
                <p className="text-xs uppercase tracking-wider text-slate-400">Homepage Content</p>
                <div className="rounded-md border border-slate-200 p-3.5 space-y-2 text-sm">
                  <div><span className="text-slate-400 text-xs">Title</span><p className="text-slate-800">{snapshot.title || "—"}</p></div>
                  <div><span className="text-slate-400 text-xs">Meta Description</span><p className="text-slate-600 text-[13px]">{snapshot.description || "—"}</p></div>
                  <div className="flex gap-4 pt-1 text-xs text-slate-500">
                    <span>H1 tags: {snapshot.h1_count}</span>
                    <span>Links: {snapshot.link_count}</span>
                  </div>
                </div>

                <p className="text-xs uppercase tracking-wider text-slate-400 pt-2">Health Issues</p>
                <div className="space-y-1.5">
                  {(snapshot.issues || []).length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle2 size={15} /> No major issues detected</div>
                  ) : snapshot.issues.map((iss, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-amber-700" data-testid="audit-issue">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{iss}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pages list */}
              <div className="lg:col-span-2">
                <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Discovered Pages ({(snapshot.pages || []).length})</p>
                <div className="rounded-md border border-slate-200 divide-y max-h-80 overflow-y-auto scroll-thin">
                  {(snapshot.pages || []).map((p) => {
                    const isNew = (snapshot.new_pages || []).includes(p.url);
                    return (
                      <a key={p.url} href={p.url} target="_blank" rel="noreferrer" data-testid="audit-page"
                        className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 text-sm">
                        <ExternalLink size={13} className="text-slate-300 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-slate-800 truncate">{p.title || p.url}</p>
                          <p className="text-[11px] text-slate-400 truncate">{p.url}</p>
                        </div>
                        {isNew && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">NEW</span>}
                        {p.is_home && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">HOME</span>}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Backlinks */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-5">
          <Link2 size={18} style={{ color: "var(--tenant-primary)" }} />
          <p className="font-display font-semibold text-slate-900">Backlink Profile</p>
        </div>
        {!backlinks || backlinks.total === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No backlinks tracked yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              {[["Total Backlinks", backlinks.total, "#0EA5E9"], ["Referring Domains", backlinks.referring_domains, "#8B5CF6"],
                ["Avg. Authority", backlinks.avg_authority, "#10B981"], ["Dofollow / Nofollow", `${backlinks.dofollow} / ${backlinks.nofollow}`, "#F59E0B"]].map(([l, v, c]) => (
                <div key={l} className="rounded-md border border-slate-200 p-3.5" data-testid="backlink-stat">
                  <p className="text-xs text-slate-400">{l}</p>
                  <p className="font-display text-2xl font-bold text-slate-900 mt-1" style={{ color: c }}>{v}</p>
                </div>
              ))}
            </div>
            <div className="rounded-md border border-slate-200 divide-y max-h-96 overflow-y-auto scroll-thin">
              {backlinks.links.map((b) => (
                <a key={b.id} href={b.url} target="_blank" rel="noreferrer" data-testid="backlink-row"
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-sm">
                  <span className="h-9 w-9 rounded flex items-center justify-center text-xs font-bold shrink-0"
                    style={{ background: `${b.authority >= 80 ? "#10B981" : b.authority >= 60 ? "#F59E0B" : "#94A3B8"}1a`, color: b.authority >= 80 ? "#10B981" : b.authority >= 60 ? "#F59E0B" : "#64748b" }}>{b.authority}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-800 truncate">{b.referring_domain}</p>
                    <p className="text-[11px] text-slate-400 truncate">{b.anchor}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${b.type === "dofollow" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{b.type}</span>
                </a>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
